"use client";

import { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

interface ExpandableServicesListProps {
  services: string[];
  itemsPerPage?: number;
  layout?: "grid" | "list";
}

export default function ExpandableServicesList({
  services,
  itemsPerPage = 4,
  layout = "grid",
}: ExpandableServicesListProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState<string>("auto");
  const contentRef = useRef<HTMLDivElement | HTMLUListElement>(null);
  const innerRef = useRef<HTMLDivElement | HTMLUListElement>(null);

  const displayedServices = isExpanded
    ? services
    : services.slice(0, itemsPerPage);

  const hasMore = services.length > itemsPerPage;

  useEffect(() => {
    if (innerRef.current) {
      if (isExpanded) {
        // Set to actual scroll height when expanded
        setMaxHeight(`${innerRef.current.scrollHeight}px`);
      } else {
        // For collapsed state, measure the height of first itemsPerPage items
        const firstItems = services.slice(0, itemsPerPage);
        // Use a temporary calculation or measure actual rendered height
        if (layout === "grid") {
          const itemsPerRow = 3; // lg:grid-cols-3
          const rows = Math.ceil(itemsPerPage / itemsPerRow);
          const itemHeight = 32; // Approximate height per item including gap
          const collapsedHeight = rows * itemHeight;
          setMaxHeight(`${collapsedHeight}px`);
        } else {
          // List layout - measure actual height
          setMaxHeight(`${innerRef.current.scrollHeight}px`);
        }
      }
    }
  }, [isExpanded, itemsPerPage, layout, services]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const containerClass =
    layout === "list"
      ? "space-y-3"
      : "grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3";

  const itemClass =
    layout === "list"
      ? "text-base text-gray-700"
      : "flex items-start text-gray-700";

  return (
    <div>
      {layout === "list" ? (
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{ maxHeight }}
        >
          <ul
            ref={innerRef as React.RefObject<HTMLUListElement>}
            className={containerClass}
          >
            {displayedServices.map((service: string, index: number) => (
              <li key={index} className={itemClass}>
                {service}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{ maxHeight }}
        >
          <div ref={innerRef as React.RefObject<HTMLDivElement>} className={containerClass}>
            {displayedServices.map((service: string, index: number) => (
              <div key={index} className={itemClass}>
                <span className="mr-2 mt-1 text-violet-600">•</span>
                <span className="text-base">{service}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleToggle}
            className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
            aria-expanded={isExpanded}
          >
            <span className="transition-transform duration-300">
              {isExpanded ? (
                <>
                  Show Less
                  <FaChevronUp className="ml-2 inline h-4 w-4 transition-transform duration-300" />
                </>
              ) : (
                <>
                  Show More
                  <FaChevronDown className="ml-2 inline h-4 w-4 transition-transform duration-300" />
                </>
              )}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

